<template>
  <form
    ref="formRef"
    class="standard-form"
    method="post"
    :action="`/my/wallet/activate-plus-pass`"
    @submit.prevent="handleSubmit"
  >
    <input type="hidden" name="plusPassId" :value="plusPassId" />
    <csrf-input />
    <button :class="`btn ${btnClasses}`" @click.prevent="openModal">
      {{ activatingBtnText }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useModalsStore } from '@/stores/modals'
import CsrfInput from '@/components/forms/csrf-input.vue'
import { useUserAuthStore } from '@/stores/user-auth'

/** EXPOSE **/
// allow to be called from parent component
defineExpose({
  openModal,
})

/** PROPS **/
const props = withDefaults(
  defineProps<{
    plusPassId: number
    activationMessage?: string
    btnClasses?: string
    btnText?: string
  }>(),
  {
    activationMessage: '',
    btnClasses: '-anchor-link',
    btnText: 'Ready to Activate',
  },
)

/** STATE **/
const submitted = ref(false)
const formRef = ref<HTMLFormElement | null>(null)
const modalsStore = useModalsStore()
const userAuthStore = useUserAuthStore()

/** COMPUTED **/
const modalId = computed(() => `activate-plus-pass-${props.plusPassId}-modal`)

const activatingBtnText = computed(() => {
  if (submitted.value) {
    return 'Processing...'
  }

  if (props.activationMessage) {
    return 'Unable to Activate'
  }

  return props.btnText
})

/** WATCHERS **/
watch(
  () => modalsStore.simpleModal.action,
  (newAction: string, prevAction: string) => {
    if (
      modalsStore.simpleModal.id === modalId.value &&
      newAction !== prevAction &&
      newAction === 'confirm' &&
      !submitted.value
    ) {
      handleSubmit()
    }
  },
)

/** METHODS **/
function handleSubmit() {
  if (submitted.value) {
    return
  }

  submitted.value = true

  try {
    // wait for vue to update before form submission
    nextTick(() => {
      if (formRef.value) {
        formRef.value.submit()
      }
    })
  } catch (err) {
    submitted.value = false
  }
}

function openModal() {
  // no double submit
  if (submitted.value) {
    return
  }

  // restricted, cannot activate
  if (props.activationMessage) {
    modalsStore.openSimpleModal({
      type: 'STANDARD',
      id: modalId.value,
      props: {
        heading: 'Unable to Activate',
        showFooter: true,
        bodySlot: props.activationMessage,
      },
    })
    return
  }

  // plus free access message to help users NOT activate their passes when
  // they have free access
  const plusFreeAccessMsg =
    userAuthStore.plusFreeAccess.enabled && userAuthStore.plusFreeAccess.availableEndDate
      ? `<br /><br /><b>Heads up! FB Plus is currently free until ${userAuthStore.plusFreeAccess.availableEndDate} Pacific, but you can still activate your pass if you want to.</b>`
      : ''

  // default
  modalsStore.openSimpleModal({
    type: 'CONFIRM',
    id: modalId.value,
    props: {
      heading: 'Activate FB Plus Pass',
      subheading: `Activating this FB Plus Pass will give you instant access to all FB Plus features and content. ${plusFreeAccessMsg}`,
      buttonText: 'Activate',
    },
  })
}
</script>
